import { openUrl } from "@/utils";
import { isNativeMobileApp } from "@/utils/platform.helpers";
import { isUnsafeUrl } from "@/utils/validators";
import { postAgelessAssessmentsDefineForCurrentUser } from "api/methods";
import type { Models } from "api/types";

import type {
  AgelessCapabilities,
  AgelessFlowType,
} from "../useAgelessCapabilities/useAgelessCapabilities";

export type StartAgelessAssessmentVariables = {
  capabilities: AgelessCapabilities;
  flowTypeOverride?: Models<"AgelessAssessmentFlowType">;
};

export function canStartAgelessAssessment(
  capabilities: AgelessCapabilities | null,
): capabilities is AgelessCapabilities {
  return (
    capabilities !== null &&
    capabilities.isEnabled &&
    !capabilities.isSubscriptionPaused &&
    capabilities.flowType !== "none" &&
    Boolean(capabilities.flowUrl)
  );
}

export async function startAgelessAssessmentForCurrentUser({
  capabilities,
  flowTypeOverride,
}: StartAgelessAssessmentVariables): Promise<{ redirectUrl: string }> {
  if (!canStartAgelessAssessment(capabilities)) {
    throw new Error("Cannot start an Ageless assessment without launchable capabilities");
  }

  const { redirectUrl } = await postAgelessAssessmentsDefineForCurrentUser({
    flowType: flowTypeOverride ?? mapAgelessFlowTypeToAssessmentFlowType(capabilities.flowType),
    requestedLanguageCode: 0,
  });

  return { redirectUrl: getAgelessAssessmentRedirectUrl(redirectUrl) };
}

export function redirectToAgelessAssessment(redirectUrl: string): void {
  if (isNativeMobileApp()) {
    openUrl(redirectUrl);
    return;
  }

  window.location.assign(redirectUrl);
}

function getAgelessAssessmentRedirectUrl(redirectUrl: string | null | undefined): string {
  if (!redirectUrl) {
    throw new Error("Ageless assessment response did not include a redirectUrl");
  }

  if (isUnsafeUrl(redirectUrl)) {
    throw new Error("Ageless assessment response included an unsafe redirectUrl");
  }

  return redirectUrl;
}

export function mapAgelessFlowTypeToAssessmentFlowType(
  flowType: AgelessFlowType,
): Models<"AgelessAssessmentFlowType"> {
  switch (flowType) {
    case "aesthetic":
      return "Aesthetics";
    case "wellness":
      return "Wellness";
    case "both":
      return "AestheticsWellness";
    default:
      throw new Error(`Unsupported Ageless flow type: ${flowType}`);
  }
}
